import React from 'react'

export const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
]

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/
const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/

export interface BillingFormValues {
  billingName: string
  billingAddress: string
  billingState: string
  billingPan: string
  billingGstin: string
}

interface BillingDetailsFormProps {
  values: BillingFormValues
  onChange: (values: BillingFormValues) => void
  errors: Partial<Record<keyof BillingFormValues, string>>
  accentColor?: 'emerald' | 'orange'
}

export function validateBilling(v: BillingFormValues): Partial<Record<keyof BillingFormValues, string>> {
  const e: Partial<Record<keyof BillingFormValues, string>> = {}
  const name = (v.billingName || '').trim()
  const address = (v.billingAddress || '').trim()
  const pan = (v.billingPan || '').trim().toUpperCase()
  const gstin = (v.billingGstin || '').trim().toUpperCase()

  if (!name || name.length < 2) e.billingName = 'Name is required'
  if (!address || address.length < 10) e.billingAddress = 'Full address required (min 10 chars)'
  if (!v.billingState) e.billingState = 'Select state'
  if (!PAN_REGEX.test(pan)) e.billingPan = 'Invalid PAN (e.g. ABCDE1234F)'
  if (gstin && !GSTIN_REGEX.test(gstin)) e.billingGstin = 'Invalid GSTIN format'
  return e
}

export const BillingDetailsForm: React.FC<BillingDetailsFormProps> = ({
  values,
  onChange,
  errors,
  accentColor = 'orange',
}) => {
  const accent = accentColor === 'emerald' ? 'focus:border-emerald-500' : 'focus:border-primary'
  const labelColor = accentColor === 'emerald' ? 'text-emerald-400/70' : 'text-primary/70'

  const field = (
    key: keyof BillingFormValues,
    label: string,
    placeholder: string,
    optional = false
  ) => (
    <div>
      <label className={`text-[9px] font-black uppercase tracking-widest ${labelColor} block mb-1`}>
        {label}{optional && <span className="text-text-secondary ml-1">(optional)</span>}
      </label>
      <input
        type="text"
        placeholder={placeholder}
        value={values[key]}
        onChange={(e) => {
          let val = e.target.value
          if (key === 'billingPan' || key === 'billingGstin') {
            val = val.toUpperCase()
          }
          onChange({ ...values, [key]: val })
        }}
        className={`w-full bg-black/40 border ${errors[key] ? 'border-red-500/50' : 'border-white/10'} text-white rounded-xl px-3 py-2 text-[11px] font-bold uppercase tracking-wider focus:outline-none ${accent} placeholder-white/20 transition-colors`}
      />
      {errors[key] && <p className="text-[9px] font-bold text-red-400 mt-1 uppercase tracking-wider">{errors[key]}</p>}
    </div>
  )

  return (
    <div className="space-y-2 mt-3 p-3 rounded-xl border border-white/10 bg-black/20">
      <p className={`text-[9px] font-black uppercase tracking-widest ${labelColor} mb-2`}>📋 Billing Details</p>

      {field('billingName', 'Full / Company Name', 'JOHN DOE / ACME PVT LTD')}

      <div>
        <label className={`text-[9px] font-black uppercase tracking-widest ${labelColor} block mb-1`}>State (Place of Supply)</label>
        <select
          value={values.billingState}
          onChange={(e) => onChange({ ...values, billingState: e.target.value })}
          className={`w-full bg-black/40 border ${errors.billingState ? 'border-red-500/50' : 'border-white/10'} text-white rounded-xl px-3 py-2 text-[11px] font-bold focus:outline-none ${accent} transition-colors`}
        >
          <option value="">— Select State —</option>
          {INDIAN_STATES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        {errors.billingState && <p className="text-[9px] font-bold text-red-400 mt-1 uppercase tracking-wider">{errors.billingState}</p>}
      </div>

      {field('billingAddress', 'Billing Address', 'FLAT 1, STREET, CITY - 600001')}
      {field('billingPan', 'PAN Number', 'ABCDE1234F')}
      {field('billingGstin', 'GSTIN', '33ABCDE1234F1Z5', true)}
    </div>
  )
}
